import React, { useMemo } from 'react';
import { ButtonType } from '../types';
import { SegmentContextType } from 'flow/components/call-history-v2/types';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useModal } from 'flow/contexts/ModalProvider';
import { segmentTrack } from 'flow/actions/segment-actions';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { selectOwnUserHasCapabilityTickets } from 'flow/selectors/TicketsSelectors';
import {
    makeSelectOwnUserIsMemberOfQueue,
    selectQueueDescription,
} from 'flow/selectors/queue-selectors';
import useAppContext from 'flow/hooks/useAppContext';
import ButtonBase from 'flow/components/pbx/v2/detail-page/call-record-action/ButtonBase';
import MenuItem from 'flow/components/ui-components/MenuItem';
import Tooltip from 'flow/components/ui-components/Tooltip';
import IconButtonCreateTicket from './IconButtonCreateTicket';

interface Props {
    queueKey: QueueKey;
    callRecordKey: CallRecordKey;
    callerNumber?: string;
    callTimestamp?: string;
    buttonType: ButtonType;
    segmentContext: SegmentContextType;
}

const ButtonCreateTicket = ({
    queueKey,
    callRecordKey,
    callerNumber,
    callTimestamp,
    buttonType,
    segmentContext,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const hasCapabilityTickets = useAppSelector(
        selectOwnUserHasCapabilityTickets,
    );

    const selectOwnExtensionIsMemberOfQueue = useMemo(
        makeSelectOwnUserIsMemberOfQueue,
        [],
    );
    const ownExtensionIsMemberOfQueue = useAppSelector((state) =>
        selectOwnExtensionIsMemberOfQueue(state, queueKey),
    );
    const queueName = useAppSelector((state) =>
        selectQueueDescription(state, queueKey),
    );

    if (!hasCapabilityTickets) {
        return null;
    }

    const disabledTooltip = !ownExtensionIsMemberOfQueue
        ? i18next.t('tickets.create.queue_membership_required', {
              queueName,
          })
        : undefined;
    const isDisabled = !!disabledTooltip;

    const label = i18next.t('tickets.ticket.create_ticket');

    const onClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(
            segmentTrack(TICKET_STRINGS.CLICK_CREATE_TICKET, {
                component: segmentContext,
            }),
        );
        openModal('ticketCreateViaQueueCall', {
            callRecordKey,
            queueKey,
            callerNumber,
            callTimestamp,
            segmentContext,
        });
    };

    if (buttonType === 'menuItem') {
        const menuItem = (
            <MenuItem
                onClick={onClick}
                disabled={isDisabled}
                closeOnClick={true}
            >
                {label}
            </MenuItem>
        );
        return isDisabled ? (
            <Tooltip label={disabledTooltip}>{menuItem}</Tooltip>
        ) : (
            menuItem
        );
    }

    if (buttonType === 'icon') {
        return (
            <IconButtonCreateTicket
                label={label}
                onClick={onClick}
                disabled={isDisabled}
                disabledTooltip={disabledTooltip}
            />
        );
    }

    const button = (
        <ButtonBase
            onClick={onClick}
            disabled={isDisabled}
            iconKey="addNoteOutlined"
            label={label}
            className="bg-gray-200 text-gray-700 hover:bg-gray-300"
        />
    );

    return isDisabled ? (
        <Tooltip label={disabledTooltip}>{button}</Tooltip>
    ) : (
        button
    );
};

export default ButtonCreateTicket;
